/**
 * The desktop popup, and the rate limiting that stops it being a reason to turn
 * notifications off.
 *
 * Three things this has to get right, and only the first is about the API:
 *
 * <b>Permission is only grantable from a gesture.</b> Chrome ignores — and in some
 * versions permanently denies — a `requestPermission()` call that did not come from
 * a click. A page that asks on load therefore does not get a maybe, it gets a no it
 * cannot ask again about. So nothing here requests anything; the caller requests it
 * from a button, and <see cref="permissionState"/> is what the UI reads to decide
 * whether to offer one.
 *
 * <b>A burst must not be a burst of popups.</b> Ten messages in one conversation is
 * one thing that happened, and ten conversations going at once is a busy minute, not
 * ten interruptions. So: same `tag` replaces rather than stacks — the browser does
 * that for us — and distinct tags are budgeted, with the overflow collapsed into one
 * summary popup. Without this, a broadcast reply wave is forty popups an agent has
 * to dismiss one at a time, and the feature is dead by the end of the afternoon.
 *
 * <b>A popup must be able to take you to the thing.</b> Clicking one focuses the tab
 * and navigates; a notification that only says something happened and leaves you to
 * find it is a notification that made you do more work, not less.
 */

/** How many distinct popups may appear in one window before they are collapsed. */
const Budget = 3;
const WindowMs = 5000;

/** Timestamps of the popups shown in the current window, oldest first. */
let shown: number[] = [];

/** Tags already on screen; these replace rather than count against the budget. */
const live = new Map<string, number>();

let suppressed = 0;
let summaryTimer: ReturnType<typeof setTimeout> | undefined;

export type PermissionState = NotificationPermission | 'unsupported';

export function permissionState(): PermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Asks the browser. <b>Call this from a click handler and nowhere else.</b>
 *
 * Resolves to the resulting state, including when the user dismisses the prompt
 * without choosing — which leaves it at `default`, and is not an error.
 */
export async function requestPermission(): Promise<PermissionState> {
  if (permissionState() === 'unsupported') return 'unsupported';

  try {
    return await Notification.requestPermission();
  } catch {
    // Safari's older callback-only form rejects the promise overload.
    return Notification.permission;
  }
}

export interface Popup {
  title: string;
  body: string;
  /** Stable for the fact being announced, so a repeat replaces rather than stacks. */
  tag: string;
  /**
   * True when this app is making the sound itself, so the OS should stay quiet.
   *
   * The two must not both make a noise, and neither must be silent. See the caller:
   * a visible tab plays the chime, a hidden one hands the job to the OS — because a
   * background tab's audio is the first thing a browser throttles, and a silent
   * notification from a minimised window is a notification nobody receives.
   */
  silent: boolean;
  onClick?: () => void;
}

/**
 * Shows one, or counts it towards a summary if too many have appeared already.
 *
 * Returns whether anything was drawn — the caller uses it for nothing, but a
 * silent no-op that cannot be distinguished from a success is the kind of thing
 * that makes this impossible to debug from a bug report.
 */
export function showPopup(popup: Popup): boolean {
  if (permissionState() !== 'granted') return false;

  const now = Date.now();
  shown = shown.filter((at) => now - at < WindowMs);

  // Forget tags that have been up a while. `live` only told us whether a popup is
  // being replaced rather than added, and without this an old tag stayed "live"
  // forever — so a conversation that had ever popped up was permanently exempt from
  // the budget, and a burst across many old conversations could never be collapsed.
  for (const [tag, at] of live) if (now - at > WindowMs * 6) live.delete(tag);

  const replacing = live.has(popup.tag);

  if (!replacing && shown.length >= Budget) {
    // Over budget. Count it and schedule one summary rather than dropping it
    // silently — "you missed four things" is information; nothing at all is not.
    suppressed += 1;
    summaryTimer ??= setTimeout(flushSummary, WindowMs);
    return false;
  }

  draw(popup);
  if (!replacing) shown.push(now);
  live.set(popup.tag, now);
  return true;
}

function draw(popup: Popup): void {
  try {
    const notification = new Notification(popup.title, {
      body: popup.body,
      tag: popup.tag,
      /*
       * True, and the first version had this exactly backwards.
       *
       * `tag` makes a second notification REPLACE the first rather than stack on it,
       * which is what stops ten messages in one chat being ten popups. `renotify`
       * decides whether that replacement is announced. With it false, the replacement
       * happens in complete silence: no banner, no sound, nothing arriving in the
       * notification centre — the existing entry is quietly rewritten in place.
       *
       * So the FIRST message in a conversation appeared and every message after it
       * was invisible. With the window in front of you that reads as "it works";
       * with the window minimised it reads as "notifications don't work when I
       * minimise", because the only one you were ever shown was the one you watched
       * arrive.
       *
       * The original reasoning — "ten replacements would be ten sounds, and the
       * sound is this app's business" — was sound about the sound and wrong about
       * everything else. The sound is handled by `silent` below, per notification.
       */
      renotify: true,
      silent: popup.silent,
    } as NotificationOptions);

    notification.onclick = () => {
      // Focus first: on Windows a click on a notification does not raise the
      // browser window by itself, so navigating without this changes a page the
      // agent cannot see.
      window.focus();
      notification.close();
      popup.onClick?.();
    };

    notification.onclose = () => { live.delete(popup.tag); };
  } catch {
    // Some Android browsers throw here and require a service worker to show a
    // notification at all. Nothing to do: the sound and the badge still fire, and
    // an exception the agent cannot act on must not reach a console they will
    // never open.
  }
}

function flushSummary(): void {
  summaryTimer = undefined;
  const count = suppressed;
  suppressed = 0;
  if (count === 0) return;

  draw({
    title: count === 1 ? '1 more update' : `${count} more updates`,
    body: 'Open the app to see them.',
    tag: 'wa:overflow',
    silent: false,
  });
}

/** Test seam: forgets the rate-limiting state. */
export function resetPopupBudget(): void {
  shown = [];
  live.clear();
  suppressed = 0;
  if (summaryTimer) clearTimeout(summaryTimer);
  summaryTimer = undefined;
}
